// TrustCenterPublic.jsx
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Common and Widgets
import Configs from '@/Configs';
import * as apiCalls from '@/common/apiCalls';
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';
import { handleApiError } from '@/common/utils/handleAPIerrors';
import GrowthLoopFooterCTA from '@/components/GrowthLoopFooterCTA';
import { getCompanyBrandFromPreferences } from '@/utils/companyBrand';

// Relative imports
import TrustCenterEngagementPulse from './components/TrustCenterEngagementPulse';
import TrustCenterHeader from './components/TrustCenterHeader';
import TrustCenterSummary from './components/TrustCenterSummary';
import TrustCenterValuesInAction from './components/TrustCenterValuesInAction';
import { useHubSpotTrustCenterTracking } from './hubspot/useHubSpotTrustCenterTracking.js';

// ------------------------------------------------------------------------------------------------
// Supporting functions
// ------------------------------------------------------------------------------------------------
function getPublicIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    const parts = url.pathname.split('/').filter(Boolean);
    // Expect: /trust-center/:publicId
    const idx = parts.findIndex((p) => p === 'trust-center');
    if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    // Fallback: last segment
    return parts[parts.length - 1] || null;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------------------------------------
// Mock data
// ------------------------------------------------------------------------------------------------
const MOCK_TRUST_CENTER = {
  company: {
    companyID: null,
    companyName: 'Kincode AI',
    logoUrl: Configs.companyLogoLarge,
  },
  personalization: {
    colorHex: '#BFC6FF',
  },
  lastSyncedAt: new Date().toISOString(),
  cultureReadiness: {
    readinessScorePercent: 92,
    summary:
      'Our culture shows strong psychological safety and alignment with strategic goals based on engagement, sentiment, and recognition data.',
  },
  engagementPulse: [
    { key: 'employee-satisfaction', label: 'Employee Satisfaction', scorePercent: 89, deltaPercent: 2.4 },
    { key: 'sense-of-belonging', label: 'Sense of Belonging', scorePercent: 94, deltaPercent: 1.1 },
    { key: 'manager-support', label: 'Manager Support', scorePercent: 88, deltaPercent: 0.0 },
    { key: 'work-life-balance', label: 'Work-Life Balance', scorePercent: 91, deltaPercent: 3.2 },
    { key: 'professional-growth', label: 'Professional Growth', scorePercent: 85, deltaPercent: -0.8 },
    { key: 'psychological-safety', label: 'Psychological Safety', scorePercent: 96, deltaPercent: 0.5 },
  ],
  valuesInAction: [
    { key: 'integrity', label: 'Integrity', livedScorePercent: 96 },
    { key: 'innovation', label: 'Innovation', livedScorePercent: 91 },
    { key: 'customer-obsession', label: 'Customer Obsession', livedScorePercent: 93 },
  ],
};

// ------------------------------------------------------------------------------------------------
// Component
// ------------------------------------------------------------------------------------------------
export function TrustCenterPublic() {
  const { t } = useTranslation(['trust-center']);
  const { setToasterMessage, companyPreferences } = useData();
  const { correlationId } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isMock, setIsMock] = useState(false);
  const [cultureStatement, setCultureStatement] = useState(null);
  const [isCultureStatementLoading, setIsCultureStatementLoading] = useState(false);

  const publicId = useMemo(() => getPublicIdFromUrl(), []);
  const companyName = data?.company?.companyName || 'Company';

  useHubSpotTrustCenterTracking({
    publicId,
    companyName: data?.company?.companyName || null,
  });

  useEffect(() => {
    async function fetchData() {
      if (!publicId) {
        setToasterMessage({ type: Configs.errorToaster, message: t('error.bad-request') });
        return;
      }

      setIsLoading(true);

      try {
        const response = await apiCalls.getPublicTrustCenterReport(publicId, correlationId);

        const status =
          typeof response === 'object' && response?.status != null ? response.status : response;

        if (status === 404) {
          // PublicId not found – show a demo Trust Center so visitors still see what this page offers.
          setToasterMessage({ type: Configs.infoToaster, message: t('trust-center.mock-notice') });
          setIsMock(true);
          setData(MOCK_TRUST_CENTER);
          return;
        }

        const error = handleApiError(response, t);
        if (error.isError) {
          setToasterMessage({ type: Configs.errorToaster, message: error.errorMessage });
          return;
        }
        setIsMock(false);
        setData(response);

        // Fire-and-forget KAI statement call; we don't block the main render on this
        setIsCultureStatementLoading(true);
        apiCalls
          .getPublicTrustCenterStatement(publicId, correlationId)
          .then((statementResp) => {
            const sStatus =
              typeof statementResp === 'object' && statementResp?.status != null
                ? statementResp.status
                : statementResp;
            if (sStatus === 200 && typeof statementResp === 'object') {
              setCultureStatement(statementResp.cultureStatement || null);
            }
          })
          .catch(() => {
            // Silent failure – we keep the placeholder text
          })
          .finally(() => {
            setIsCultureStatementLoading(false);
          });
      } catch {
        setToasterMessage({ type: Configs.errorToaster, message: t('error') });
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [publicId, correlationId, setToasterMessage, t]);

  const personalization =
    (data && data?.personalization) || companyPreferences?.personalization || null;
  const { colorHex: primaryColorHex, buttonTheme } = getCompanyBrandFromPreferences(
    personalization ? { personalization } : companyPreferences
  );
  const readiness = data?.cultureReadiness?.readinessScorePercent ?? null;
  const companyLogo =
    data?.company?.logoUrl || personalization?.logoUrl || Configs.companyLogoLarge;

  function handleShare() {
    try {
      const href = window?.location?.href;
      if (!href) {
        return;
      }

      const url = encodeURIComponent(href);

      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
        '_blank',
        'noopener,noreferrer'
      );
    } catch {
      setToasterMessage({ type: Configs.errorToaster, message: t('error') });
    }
  }

  // ------------------------------------------------------------------------------------------------
  // Render the component
  // ------------------------------------------------------------------------------------------------
  return (
    <div
      className="flex h-dvh flex-col overflow-y-auto bg-gray-50 dark:bg-gray-950"
      data-testid="trust-center-trust-center-public"
    >
      <div
        className="max-w-6xl mx-auto px-4 py-8 flex-1 w-full"
        data-testid="trust-center-trust-center-public-content"
      >
        <TrustCenterHeader
          companyName={companyName}
          companyLogo={companyLogo}
          isMock={isMock}
          primaryColorHex={primaryColorHex}
          buttonTheme={buttonTheme}
          onShare={handleShare}
          title={t('trust-center.title', { companyName })}
          subtitle={t('trust-center.subtitle')}
        />

        <TrustCenterSummary
          isLoading={isLoading}
          readiness={readiness}
          summary={data?.cultureReadiness?.summary}
          colorHex={primaryColorHex}
          cultureReadiness={data?.cultureReadiness || null}
          cultureStatement={cultureStatement}
          isCultureStatementLoading={isCultureStatementLoading}
        />

        {/* Values (second section) */}
        <TrustCenterValuesInAction
          isLoading={isLoading}
          values={data?.valuesInAction || []}
          colorHex={primaryColorHex}
        />

        {/* Engagement Pulse (drivers last) */}
        <TrustCenterEngagementPulse
          isLoading={isLoading}
          kpis={data?.engagementPulse || []}
          colorHex={primaryColorHex}
        />
      </div>
      <GrowthLoopFooterCTA />
    </div>
  );
}
